import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { api, post } from '../lib/api'
import { Button, Card, EmptyState, Skeleton, useToast } from '../ui'
import { Page } from './Page'
import styles from './CalendarPage.module.css'

type ActionType = 'dividend' | 'bonus' | 'split' | 'buyback' | 'rights' | 'results' | 'agm' | 'egm' | 'meeting' | 'demerger'
type View = 'week' | 'month' | 'list'

interface CalAction {
  id: string; symbol: string; name: string; action_type: ActionType; description: string
  ex_date: string | null; record_date: string | null; meeting_date: string | null; payment_date: string | null
  ratio: string | null; amount: number | null; exchange: string; event_date: string; held: boolean; watched: boolean
}
interface Holiday { date: string; description: string; exchange: string; session: string }
interface CalResponse { view: View; from: string; to: string; data: CalAction[]; holidays: Holiday[]; connected: boolean }

/** Legend groups (DESIGN_SPEC chip colours); each filters one or more normalised types. */
const LEGEND: Array<{ id: string; label: string; types: ActionType[] }> = [
  { id: 'dividend', label: 'Dividend', types: ['dividend'] },
  { id: 'bonus', label: 'Bonus', types: ['bonus'] },
  { id: 'split', label: 'Split', types: ['split'] },
  { id: 'buyback', label: 'Buyback', types: ['buyback'] },
  { id: 'rights', label: 'Rights', types: ['rights', 'demerger'] },
  { id: 'results', label: 'Results', types: ['results'] },
  { id: 'agm', label: 'AGM/EGM', types: ['agm', 'egm'] },
  { id: 'meeting', label: 'Meeting', types: ['meeting'] },
]
const GROUP_OF: Record<ActionType, string> = {
  dividend: 'dividend', bonus: 'bonus', split: 'split', buyback: 'buyback', rights: 'rights', demerger: 'rights',
  results: 'results', agm: 'agm', egm: 'agm', meeting: 'meeting',
}
const TYPE_LABEL: Record<ActionType, string> = {
  dividend: 'Dividend', bonus: 'Bonus', split: 'Split', buyback: 'Buyback', rights: 'Rights', demerger: 'Demerger',
  results: 'Results', agm: 'AGM', egm: 'EGM', meeting: 'Meeting',
}
// Held first, then watched, then corporate actions ahead of routine meetings.
const TYPE_RANK: Record<ActionType, number> = { bonus: 0, split: 1, buyback: 2, rights: 3, demerger: 4, dividend: 5, results: 6, agm: 7, egm: 8, meeting: 9 }
const rank = (a: CalAction) => (a.held ? 0 : a.watched ? 100 : 200) + TYPE_RANK[a.action_type]

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const parse = (d: string) => new Date(`${d}T00:00:00Z`)
const ymd = (d: Date) => d.toISOString().slice(0, 10)
const addDays = (d: string, n: number) => { const x = parse(d); x.setUTCDate(x.getUTCDate() + n); return ymd(x) }
const dow = (d: string) => (parse(d).getUTCDay() + 6) % 7
const short = (d: string | null) => { if (!d) return '—'; const x = parse(d); return `${x.getUTCDate()} ${MONTHS[x.getUTCMonth()]}` }
const todayIST = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date())
const isDay = (s: string | null): s is string => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s) && !isNaN(Date.parse(s))

function rangeLabel(view: View, from: string, to: string) {
  const a = parse(from), b = parse(to)
  if (view === 'month') return `${['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][a.getUTCMonth()]} ${a.getUTCFullYear()}`
  const lastWeekday = view === 'week' ? parse(addDays(from, 4)) : b
  const sameMonth = a.getUTCMonth() === lastWeekday.getUTCMonth()
  return `${a.getUTCDate()}${sameMonth ? '' : ` ${MONTHS[a.getUTCMonth()]}`} – ${lastWeekday.getUTCDate()} ${MONTHS[lastWeekday.getUTCMonth()]} ${lastWeekday.getUTCFullYear()}`
}

/** One-line detail in the mockup style ("Ex-date · ₹6 per share"). */
function detail(a: CalAction): string {
  const amt = a.amount != null ? `₹${a.amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : null
  switch (a.action_type) {
    case 'dividend': return `Ex-date · ${amt ? `${amt} per share` : a.description}`
    case 'bonus': return `Ex-date · ${a.ratio ? `${a.ratio} bonus` : a.description}`
    case 'split': return `Ex-date · ${a.description}`
    case 'rights': return `Ex-date · ${a.ratio ? `rights ${a.ratio}${amt ? ` @ ${amt}` : ''}` : a.description}`
    case 'buyback': return a.ex_date ? `Record/ex-date · ${a.description}` : a.description
    case 'results': return a.description.replace(/^Board meeting — /i, 'Board meeting · ')
    default: return a.description
  }
}

function Chip({ type }: { type: ActionType }) {
  return <span className={`${styles.chip} ${styles[`t_${GROUP_OF[type]}`]}`}>{TYPE_LABEL[type]}</span>
}

function Tags({ a }: { a: CalAction }) {
  return <>{a.held && <span className={styles.holding}>HOLDING</span>}{!a.held && a.watched && <span className={styles.watch}>WATCHLIST</span>}</>
}

function EventCard({ a }: { a: CalAction }) {
  return (
    <li className={`${styles.event} ${a.held ? styles.mine : ''}`}>
      <div className={styles.eventHead}><Link to={`/stock/${encodeURIComponent(a.symbol)}`} className={styles.sym}>{a.symbol}</Link><Tags a={a} /></div>
      <Chip type={a.action_type} />
      <span className={styles.detail}>{detail(a)}</span>
    </li>
  )
}

function SyncPanel({ onClose }: { onClose: () => void }) {
  const toast = useToast()
  const [url, setUrl] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => { api<{ url: string }>('/calendar/feed').then(r => setUrl(r.url)).catch(() => toast.show({ tone: 'error', message: 'Could not create your calendar link' })) }, [toast])
  const webcal = url?.replace(/^https:/, 'webcal:')
  const copy = async () => { if (!url) return; try { await navigator.clipboard.writeText(url); toast.show({ tone: 'success', message: 'Calendar link copied' }) } catch { toast.show({ tone: 'error', message: 'Copy failed — select the link and copy it' }) } }
  const rotate = async () => {
    setBusy(true)
    try { setUrl((await post<{ url: string }>('/calendar/feed/rotate', {})).url); toast.show({ tone: 'success', message: 'New link created. The old link no longer works.' }) }
    catch { toast.show({ tone: 'error', message: 'Could not reset the link' }) } finally { setBusy(false) }
  }
  return (
    <Card title="Sync to your calendar" action={<Button size="sm" variant="ghost" onClick={onClose}>Close</Button>}>
      <div className={styles.sync}>
        <p>Subscribe to this private link in Google Calendar, Apple Calendar or Outlook. It lists ex-dates, record dates and board meetings for the stocks in your holdings and watchlists, and refreshes every few hours. Keep the link private — anyone with it can see those events.</p>
        <label className={styles.syncLabel} htmlFor="ics-url">Your calendar link</label>
        <div className={styles.syncRow}>
          <input id="ics-url" className={styles.syncInput} readOnly value={url ?? 'Creating link…'} onFocus={e => e.currentTarget.select()} />
          <Button size="sm" onClick={copy} disabled={!url}>Copy link</Button>
        </div>
        <div className={styles.syncRow}>
          {url && <a className={styles.syncLink} href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcal ?? '')}`} target="_blank" rel="noopener noreferrer">Add to Google Calendar</a>}
          {webcal && <a className={styles.syncLink} href={webcal}>Open in Apple Calendar / Outlook</a>}
          <Button size="sm" variant="ghost" onClick={rotate} loading={busy}>Reset link</Button>
        </div>
      </div>
    </Card>
  )
}

export function CalendarPage() {
  const [params, setParams] = useSearchParams()
  const view: View = (['week', 'month', 'list'] as const).find(v => v === params.get('view')) ?? 'week'
  const today = todayIST()
  const start = isDay(params.get('start')) ? params.get('start')! : today
  const mine = params.get('mine') === '1'
  const groups = useMemo(() => (params.get('types') ?? '').split(',').filter(g => LEGEND.some(l => l.id === g)), [params])
  const types = useMemo(() => LEGEND.filter(l => groups.includes(l.id)).flatMap(l => l.types), [groups])

  const [data, setData] = useState<CalResponse | null>(null)
  const [upcoming, setUpcoming] = useState<CalResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sync, setSync] = useState(false)

  const qs = useCallback((v: View, s: string) => {
    const q = new URLSearchParams({ view: v, start: s })
    if (types.length) q.set('types', types.join(','))
    if (mine) q.set('mine', '1')
    return q.toString()
  }, [types, mine])

  useEffect(() => {
    let live = true
    setError(null)
    api<CalResponse>(`/calendar?${qs(view, start)}`).then(r => { if (live) setData(r) }).catch(e => { if (live) setError((e as Error).message) })
    return () => { live = false }
  }, [qs, view, start])
  useEffect(() => {
    let live = true
    api<CalResponse>(`/calendar?${qs('list', today)}`).then(r => { if (live) setUpcoming(r) }).catch(() => undefined)
    return () => { live = false }
  }, [qs, today])

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) { if (v == null || v === '') next.delete(k); else next.set(k, v) }
    setParams(next, { replace: true })
  }
  const toggleGroup = (id: string) => {
    const set = new Set(groups)
    if (set.has(id)) set.delete(id); else set.add(id)
    update({ types: [...set].join(',') || null })
  }
  const shift = (dir: -1 | 1) => {
    if (!data) return
    if (view === 'month') { const d = parse(data.from); d.setUTCMonth(d.getUTCMonth() + dir); update({ start: ymd(d) }) }
    else update({ start: addDays(data.from, dir * (view === 'week' ? 7 : 60)) })
  }

  const byDay = useMemo(() => {
    const m = new Map<string, CalAction[]>()
    for (const a of data?.data ?? []) { const k = String(a.event_date).slice(0, 10); m.set(k, [...(m.get(k) ?? []), a]) }
    for (const list of m.values()) list.sort((x, y) => rank(x) - rank(y) || x.symbol.localeCompare(y.symbol))
    return m
  }, [data])
  const holidayOn = useMemo(() => {
    const m = new Map<string, Holiday>()
    for (const h of data?.holidays ?? []) if (h.exchange === 'NSE' && h.session === 'full') m.set(h.date, h)
    return m
  }, [data])
  // First weekday on/after today that is not an NSE holiday.
  const nextSession = useMemo(() => {
    let d = today
    for (let i = 0; i < 10; i++) { if (dow(d) < 5 && !holidayOn.has(d)) return d; d = addDays(d, 1) }
    return today
  }, [today, holidayOn])

  const upcomingRows = useMemo(() => {
    const until = addDays(today, 21)
    return (upcoming?.data ?? []).filter(a => String(a.event_date).slice(0, 10) <= until).sort((x, y) => rank(x) - rank(y) || String(x.event_date).localeCompare(String(y.event_date)))
  }, [upcoming, today])
  const TABLE_MAX = 25

  const openDay = (d: string) => update({ view: 'list', start: d })

  const subtitle = <>Dividends, bonus, splits, buybacks, rights, results and meetings from NSE and BSE · ex-date = record date under T+1</>

  return (
    <Page title="Corporate actions calendar" subtitle={subtitle} actions={<Button onClick={() => setSync(s => !s)} aria-expanded={sync}>Sync to Google Calendar</Button>}>
      {sync && <SyncPanel onClose={() => setSync(false)} />}

      <div className={styles.toolbar}>
        <div className={styles.nav}>
          <button type="button" className={styles.navBtn} aria-label={`Previous ${view === 'list' ? 'period' : view}`} onClick={() => shift(-1)} disabled={!data}>‹</button>
          <span className={styles.range} aria-live="polite">{data ? rangeLabel(view, data.from, data.to) : '…'}</span>
          <button type="button" className={styles.navBtn} aria-label={`Next ${view === 'list' ? 'period' : view}`} onClick={() => shift(1)} disabled={!data}>›</button>
          <button type="button" className={styles.todayBtn} onClick={() => update({ start: null })}>Today</button>
          <div role="group" aria-label="View" className={styles.seg}>
            {(['week', 'month', 'list'] as const).map(v => (
              <button key={v} type="button" aria-pressed={view === v} className={view === v ? styles.segOn : styles.segBtn} onClick={() => update({ view: v === 'week' ? null : v })}>
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.filters}>
          <div role="group" aria-label="Filter by type" className={styles.legend}>
            {LEGEND.map(l => (
              <button key={l.id} type="button" aria-pressed={groups.includes(l.id)} onClick={() => toggleGroup(l.id)}
                className={`${styles.legendBtn} ${styles[`t_${l.id}`]} ${groups.length && !groups.includes(l.id) ? styles.legendOff : ''}`}>
                {l.label}
              </button>
            ))}
            {groups.length > 0 && <button type="button" className={styles.clear} onClick={() => update({ types: null })}>Show all</button>}
          </div>
          <label className={styles.mineToggle}><input type="checkbox" checked={mine} onChange={e => update({ mine: e.target.checked ? '1' : null })} />My holdings only</label>
        </div>
      </div>

      {error && <Card variant="flush"><EmptyState kind="error" title="Could not load the calendar" compact>{error}</EmptyState></Card>}
      {!error && !data && <Card><Skeleton lines={6} label="Loading calendar" /></Card>}
      {data && !data.connected && <Card variant="flush"><EmptyState kind="not-connected" title="Data not connected">Corporate-actions source not connected yet.</EmptyState></Card>}

      {data?.connected && view === 'week' && (() => {
        const days = Array.from({ length: 7 }, (_, i) => addDays(data.from, i)).filter(d => dow(d) < 5 || (byDay.get(d)?.length ?? 0) > 0)
        return (
          <section aria-label="Week view" className={styles.week} style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}>
            {days.map(d => {
              const list = byDay.get(d) ?? [], hol = holidayOn.get(d), shown = list.slice(0, 6)
              return (
                <div key={d} className={`${styles.day} ${hol || dow(d) > 4 ? styles.dayOff : ''}`} aria-label={`${DOW[dow(d)]} ${short(d)}`} role="group">
                  <div className={styles.dayHead}>
                    <span className={styles.dayNum}>{parse(d).getUTCDate()}</span><span className={styles.dayName}>{DOW[dow(d)]}</span>
                    {d === nextSession && <span className={styles.next}>{d === today ? 'TODAY' : 'NEXT SESSION'}</span>}
                  </div>
                  <ul className={styles.events}>
                    {hol && <li className={styles.holiday}>Market holiday<br />{hol.description}</li>}
                    {shown.map(a => <EventCard key={a.id} a={a} />)}
                    {!hol && list.length === 0 && <li className={styles.none}>No actions</li>}
                  </ul>
                  {list.length > shown.length && <button type="button" className={styles.more} onClick={() => openDay(d)}>+{list.length - shown.length} more</button>}
                </div>
              )
            })}
          </section>
        )
      })()}

      {data?.connected && view === 'month' && (() => {
        const first = data.from, lead = dow(first)
        const cells = Array.from({ length: Math.ceil((lead + parse(data.to).getUTCDate()) / 7) * 7 }, (_, i) => addDays(first, i - lead))
        const month = parse(first).getUTCMonth()
        return (
          <section aria-label="Month view" className={styles.month}>
            {DOW.map(n => <div key={n} className={styles.monthDow} aria-hidden="true">{n}</div>)}
            {cells.map(d => {
              const inMonth = parse(d).getUTCMonth() === month
              const list = inMonth ? byDay.get(d) ?? [] : [], hol = inMonth ? holidayOn.get(d) : undefined
              return (
                <div key={d} className={`${styles.cell} ${!inMonth ? styles.cellOut : ''} ${hol || dow(d) > 4 ? styles.dayOff : ''} ${d === today ? styles.cellToday : ''}`}>
                  <span className={styles.cellNum}>{parse(d).getUTCDate()}</span>
                  {hol && <span className={styles.cellHol}>{hol.description}</span>}
                  <ul className={styles.cellList}>
                    {list.slice(0, 3).map(a => (
                      <li key={a.id}><Link to={`/stock/${encodeURIComponent(a.symbol)}`} className={styles.cellEv} title={`${a.symbol} · ${TYPE_LABEL[a.action_type]} · ${detail(a)}`}>
                        <span className={`${styles.dot} ${styles[`t_${GROUP_OF[a.action_type]}`]}`} aria-hidden="true" />{a.symbol}{a.held && <span className={styles.srOnly}> (holding)</span>}
                        <span className={styles.srOnly}> — {TYPE_LABEL[a.action_type]}</span>
                      </Link></li>
                    ))}
                  </ul>
                  {list.length > 3 && <button type="button" className={styles.more} onClick={() => openDay(d)} aria-label={`${list.length - 3} more on ${short(d)}`}>+{list.length - 3} more</button>}
                </div>
              )
            })}
          </section>
        )
      })()}

      {data?.connected && view === 'list' && (
        <Card variant="flush" title={`${data.data.length} actions`}>
          {data.data.length === 0 ? <EmptyState compact title="No actions in this period">Try another date range or clear the filters.</EmptyState> : (
            <ActionTable rows={data.data} showDate />
          )}
        </Card>
      )}

      {data?.connected && view !== 'list' && (
        <Card variant="flush" title="Upcoming corporate actions"
          action={<span className={styles.meta}>Next 3 weeks · {Math.min(upcomingRows.length, TABLE_MAX)} of {upcomingRows.length} shown</span>}>
          {!upcoming ? <Skeleton lines={4} label="Loading upcoming actions" /> : upcomingRows.length === 0
            ? <EmptyState compact title="Nothing in the next 3 weeks">{mine ? 'None of your holdings has an action scheduled.' : 'No actions match the filters.'}</EmptyState>
            : <>
              <ActionTable rows={upcomingRows.slice(0, TABLE_MAX)} />
              {upcomingRows.length > TABLE_MAX && <div className={styles.tableFoot}><button type="button" className={styles.more} onClick={() => update({ view: 'list', start: null })}>Show all {upcomingRows.length} in list view</button></div>}
            </>}
        </Card>
      )}
    </Page>
  )
}

function ActionTable({ rows, showDate = false }: { rows: CalAction[]; showDate?: boolean }) {
  return (
    <div className={styles.tableWrap} tabIndex={0} role="region" aria-label="Corporate actions table (scrolls sideways)">
      <table className={styles.table}>
        <thead><tr>
          {showDate && <th scope="col">Date</th>}
          <th scope="col">Company</th><th scope="col">Purpose</th><th scope="col">Ex-date</th><th scope="col">Record date</th><th scope="col">Details</th><th scope="col">Your holdings</th>
        </tr></thead>
        <tbody>
          {rows.map(a => (
            <tr key={a.id}>
              {showDate && <td className={styles.mono}>{short(String(a.event_date).slice(0, 10))}</td>}
              <td><Link to={`/stock/${encodeURIComponent(a.symbol)}`} className={styles.sym}>{a.symbol}</Link><span className={styles.name}>{a.name}</span></td>
              <td><Chip type={a.action_type} /></td>
              <td className={styles.mono}>{short(a.ex_date)}</td>
              <td className={styles.mono}>{short(a.record_date)}</td>
              <td className={styles.detailCell}>{a.meeting_date && !a.ex_date ? `${a.description.replace(/^Board meeting — /i, 'Board meeting · ')} · ${short(a.meeting_date)}` : a.description}</td>
              <td className={a.held ? styles.yes : styles.no}>{a.held ? 'Yes · in portfolio' : a.watched ? 'In watchlist' : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
